'use client';

import {
  formatVolume,
  monthLabel,
  type PopularityBrandRow,
  type PopularityDataset,
} from '@/lib/popularity';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { drawChartCard, drawShareCard, shareCardFilename, shareCardWidth } from './shareCard';
import styles from './TrendsExplorer.module.css';

// Distinct, theme-safe line colors for the top brands on the chart.
const PALETTE = [
  '#4285f4',
  '#a142f4',
  '#f97316',
  '#0f9d58',
  '#e11d48',
  '#00acc1',
  '#eab308',
  '#7c4dff',
];

type Metric = 'share' | 'absolute';
type Tab = 'ranking' | 'movers' | 'country';

function deltaClass(v: number | null) {
  if (v == null) return styles.flat;
  if (v > 2) return styles.up;
  if (v < -2) return styles.down;
  return styles.flat;
}
function deltaText(v: number | null) {
  if (v == null) return '—';
  const arrow = v > 2 ? '▲' : v < -2 ? '▼' : '·';
  return `${arrow} ${v > 0 ? '+' : ''}${v}%`;
}

function Sparkline({ data, color }: { data: number[]; color: string }) {
  const w = 88;
  const h = 26;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const span = max - min || 1;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1 || 1)) * w;
      const y = h - 2 - ((v - min) / span) * (h - 4);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg className={styles.spark} width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={1.6}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LineChart({
  months,
  rows,
  metric,
}: {
  months: string[];
  rows: PopularityBrandRow[];
  metric: Metric;
}) {
  const [hover, setHover] = useState<{ i: number; py: number } | null>(null);
  const w = 860;
  const h = 300;
  const padL = 46;
  const padR = 14;
  const padT = 14;
  const padB = 28;
  const top = rows.slice(0, 6);

  const totals = months.map((_, i) => rows.reduce((a, r) => a + (r.monthly[i] || 0), 0) || 1);
  const valueAt = (r: PopularityBrandRow, i: number) =>
    metric === 'share' ? ((r.monthly[i] || 0) / totals[i]) * 100 : r.monthly[i] || 0;

  const maxY = Math.max(
    ...top.flatMap((r) => months.map((_, i) => valueAt(r, i))),
    metric === 'share' ? 10 : 1,
  );
  const x = (i: number) => padL + (i / (months.length - 1 || 1)) * (w - padL - padR);
  const y = (v: number) => padT + (1 - v / maxY) * (h - padT - padB);

  const step = (w - padL - padR) / (months.length - 1 || 1);
  // Map a pointer event to the nearest month index + the pointer's Y in viewBox
  // space (works through the SVG's responsive viewBox scaling — we normalise by
  // the rendered size first). The crosshair snaps to the month; the tooltip box
  // follows the cursor vertically, like a standard web chart tooltip.
  const posFromEvent = (e: ReactPointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const relX = ((e.clientX - rect.left) / rect.width) * w;
    const relY = ((e.clientY - rect.top) / rect.height) * h;
    return {
      i: Math.max(0, Math.min(months.length - 1, Math.round((relX - padL) / step))),
      py: relY,
    };
  };

  const fmt = (v: number) =>
    metric === 'share' ? `${v.toFixed(1)}%` : formatVolume(Math.round(v));

  const yTicks = 4;
  return (
    <svg
      className={styles.chart}
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label="Search demand over the last 12 months"
      onPointerMove={(e) => setHover(posFromEvent(e))}
      onPointerLeave={() => setHover(null)}
    >
      {Array.from({ length: yTicks + 1 }, (_, t) => {
        const v = (maxY / yTicks) * t;
        const yy = y(v);
        return (
          <g key={t}>
            <line
              x1={padL}
              x2={w - padR}
              y1={yy}
              y2={yy}
              stroke="var(--trd-line)"
              strokeWidth={1}
            />
            <text x={padL - 8} y={yy + 3} textAnchor="end" fontSize="10" fill="var(--text-soft)">
              {metric === 'share' ? `${Math.round(v)}%` : formatVolume(Math.round(v))}
            </text>
          </g>
        );
      })}
      {months.map((m, i) =>
        i % 2 === 0 ? (
          <text
            key={m}
            x={x(i)}
            y={h - 9}
            textAnchor="middle"
            fontSize="9.5"
            fill="var(--text-soft)"
          >
            {monthLabel(m).replace(' 20', " '")}
          </text>
        ) : null,
      )}
      {top.map((r, idx) => {
        const d = months
          .map((_, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(valueAt(r, i)).toFixed(1)}`)
          .join(' ');
        return (
          <path
            key={r.name}
            d={d}
            fill="none"
            stroke={PALETTE[idx]}
            strokeWidth={2.4}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        );
      })}
      {hover != null &&
        (() => {
          const hi = hover.i;
          const hx = x(hi);
          const pts = top
            .map((r, idx) => ({ name: r.name, color: PALETTE[idx], v: valueAt(r, hi) }))
            .sort((a, b) => b.v - a.v);
          const lineH = 15;
          const boxW = 156;
          const boxH = 22 + pts.length * lineH;
          // Anchor the box beside the cursor, flipping side / clamping so it
          // never runs off an edge or covers the pointer.
          const gap = 16;
          const flip = hx + gap + boxW > w - padR;
          const bx = flip ? hx - boxW - gap : hx + gap;
          const by = Math.max(padT, Math.min(h - padB - boxH, hover.py - boxH / 2));
          return (
            <g pointerEvents="none">
              <line
                x1={hx}
                x2={hx}
                y1={padT}
                y2={h - padB}
                stroke="var(--text-soft)"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
              {top.map((r, idx) => (
                <circle
                  key={r.name}
                  cx={hx}
                  cy={y(valueAt(r, hi))}
                  r={3.4}
                  fill={PALETTE[idx]}
                  stroke="var(--trd-card)"
                  strokeWidth={1.6}
                />
              ))}
              <rect
                x={bx}
                y={by}
                width={boxW}
                height={boxH}
                rx={9}
                fill="var(--trd-card)"
                stroke="rgba(128,130,150,0.35)"
                strokeWidth={1}
              />
              <text x={bx + 12} y={by + 16} fontSize="10.5" fontWeight={700} fill="var(--text)">
                {monthLabel(months[hi])}
              </text>
              {pts.map((p, i) => (
                <g key={p.name}>
                  <rect
                    x={bx + 12}
                    y={by + 24 + i * lineH}
                    width={7}
                    height={7}
                    rx={2}
                    fill={p.color}
                  />
                  <text x={bx + 25} y={by + 31 + i * lineH} fontSize="10" fill="var(--text-soft)">
                    {p.name}
                  </text>
                  <text
                    x={bx + boxW - 12}
                    y={by + 31 + i * lineH}
                    textAnchor="end"
                    fontSize="10"
                    fontWeight={600}
                    fill="var(--text)"
                  >
                    {fmt(p.v)}
                  </text>
                </g>
              ))}
            </g>
          );
        })()}
    </svg>
  );
}

export default function TrendsExplorer({
  data,
  shareUrl,
}: {
  data: PopularityDataset;
  shareUrl: string;
}) {
  const [scope, setScope] = useState('WW');
  const [metric, setMetric] = useState<Metric>('share');
  const [tab, setTab] = useState<Tab>('ranking');

  const rows = data.series[scope] ?? data.series.WW;
  const dataThrough = data.months[data.months.length - 1];
  const maxAvg = Math.max(...rows.map((r) => r.avg), 1);
  const maxSqrt = Math.sqrt(maxAvg);
  const scopeName = data.countries.find((c) => c.code === scope)?.name ?? 'Worldwide';
  const totalDemand = rows.reduce((a, r) => a + r.avg, 0);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const hasNativeShare = () =>
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  useEffect(() => {
    const close = () => {
      setShareOpen(false);
      setDownloadOpen(false);
    };
    if (shareOpen || downloadOpen) window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [shareOpen, downloadOpen]);

  // Movers use the smoothed `momentum` and only "reliable" (above a volume
  // floor) items, so a tiny-base band jump never headlines the risers.
  const { risers, decliners } = useMemo(() => {
    const eligible = rows.filter((r) => r.reliable && r.momentum != null);
    const byMom = [...eligible].sort((a, b) => (b.momentum ?? 0) - (a.momentum ?? 0));
    return {
      risers: byMom.filter((r) => (r.momentum ?? 0) > 0).slice(0, 5),
      decliners: byMom
        .filter((r) => (r.momentum ?? 0) < 0)
        .reverse()
        .slice(0, 5),
    };
  }, [rows]);

  const byCountry = useMemo(
    () =>
      data.countries
        .filter((c) => c.code !== 'WW')
        .map((c) => {
          const s = data.series[c.code] ?? [];
          return { code: c.code, name: c.name, leader: s[0], second: s[1] };
        })
        .filter((c) => c.leader)
        .sort((a, b) => (b.leader.share ?? 0) - (a.leader.share ?? 0)),
    [data],
  );

  // Resolve the brand font (Manrope) that next/font injected, so the canvas
  // cards are typeset in it instead of a chunky system fallback.
  const resolveFont = () => {
    if (typeof window === 'undefined') return 'Manrope';
    const v = getComputedStyle(document.documentElement).getPropertyValue('--font-manrope').trim();
    return v || 'Manrope';
  };
  const fontsReady = () =>
    typeof document !== 'undefined' && document.fonts?.ready
      ? document.fonts.ready
      : Promise.resolve();
  const cardBase = () => ({
    rows,
    palette: PALETTE,
    scopeName,
    categoryLabel: data.label,
    dataThrough,
    metric,
    pageUrl: shareUrl,
    unit: data.unit,
    font: resolveFont(),
  });
  function renderCard(): { canvas: HTMLCanvasElement; height: number } {
    const canvas = canvasRef.current ?? document.createElement('canvas');
    const height = drawShareCard(canvas, cardBase());
    return { canvas, height };
  }
  function renderChart(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    drawChartCard(canvas, { ...cardBase(), months: data.months });
    return canvas;
  }
  function toBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  }
  function saveBlob(blob: Blob | null, filename: string) {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
  async function downloadImage() {
    await fontsReady();
    const { canvas } = renderCard();
    saveBlob(
      await toBlob(canvas),
      shareCardFilename(data.label, scopeName, dataThrough, 'ranking'),
    );
  }
  async function downloadChartImage() {
    await fontsReady();
    saveBlob(
      await toBlob(renderChart()),
      shareCardFilename(data.label, scopeName, dataThrough, 'trend'),
    );
  }
  async function downloadPdf() {
    await fontsReady();
    const { canvas, height } = renderCard();
    const { jsPDF } = await import('jspdf');
    // Full-bleed page sized to the card in points (pt) so nothing is cropped —
    // jsPDF's px unit mismatches the image DPI and clips the right/bottom edge.
    const w = shareCardWidth;
    const h = height;
    const pdf = new jsPDF({
      orientation: h > w ? 'portrait' : 'landscape',
      unit: 'pt',
      format: [w, h],
      compress: true,
    });
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h, undefined, 'FAST');
    pdf.save(shareCardFilename(data.label, scopeName, dataThrough, 'ranking', 'pdf'));
  }
  async function nativeShare() {
    await fontsReady();
    const { canvas } = renderCard();
    const blob = await toBlob(canvas);
    const title = `Cleanor Search Index — most popular ${data.label.toLowerCase()}`;
    const text = `${rows[0].name} leads ${scopeName} with ${rows[0].share}% of ${data.label.toLowerCase()} search demand (${monthLabel(dataThrough)}).`;
    const file = blob
      ? new File([blob], shareCardFilename(data.label, scopeName, dataThrough, 'ranking'), {
          type: 'image/png',
        })
      : null;
    try {
      if (file && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title, text, url: shareUrl, files: [file] });
      } else {
        await navigator.share({ title, text, url: shareUrl });
      }
    } catch {
      /* cancelled */
    }
  }
  function exportCsv() {
    const head = 'rank,name,vendor,share_pct,avg_monthly_searches,mom_pct,yoy_pct';
    const lines = rows.map(
      (r) =>
        `${r.rank},"${r.name}","${r.vendor}",${r.share},${r.avg},${r.mom ?? ''},${r.yoy ?? ''}`,
    );
    const blob = new Blob([[head, ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cleanor-search-index-${data.category}-${scope.toLowerCase()}-${dataThrough}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const shareText = `The Cleanor Search Index: ${rows[0].name} leads ${data.label.toLowerCase()} search demand (${scopeName}, ${monthLabel(dataThrough)}).`;
  const socialLinks = {
    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    reddit: `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`,
  };

  return (
    <div className={styles.console}>
      <canvas ref={canvasRef} style={{ display: 'none' }} aria-hidden="true" />

      {/* top control bar (GSC filter row) */}
      <div className={styles.bar}>
        <label className={styles.pill}>
          <span className={styles.pillLabel}>Country</span>
          <select
            className={styles.pillSelect}
            value={scope}
            onChange={(e) => setScope(e.target.value)}
          >
            {data.countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <div className={styles.segmented} role="group" aria-label="Metric">
          <button
            className={metric === 'share' ? styles.segOn : ''}
            onClick={() => setMetric('share')}
          >
            Share
          </button>
          <button
            className={metric === 'absolute' ? styles.segOn : ''}
            onClick={() => setMetric('absolute')}
          >
            Volume
          </button>
        </div>
        <div className={styles.spacer} />
        <div className={styles.toolbar} onClick={(e) => e.stopPropagation()}>
          <div className={styles.menuWrap}>
            <button
              className={styles.action}
              onClick={() => {
                setShareOpen(false);
                setDownloadOpen((v) => !v);
              }}
              type="button"
              aria-haspopup="menu"
              aria-expanded={downloadOpen}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path
                  d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Download
            </button>
            {downloadOpen && (
              <div className={styles.shareMenu} role="menu">
                <button
                  type="button"
                  onClick={() => {
                    setDownloadOpen(false);
                    void downloadImage();
                  }}
                >
                  Ranking image (PNG)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDownloadOpen(false);
                    void downloadPdf();
                  }}
                >
                  Ranking (PDF)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDownloadOpen(false);
                    void downloadChartImage();
                  }}
                >
                  Trend chart (PNG)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDownloadOpen(false);
                    exportCsv();
                  }}
                >
                  Data (CSV)
                </button>
              </div>
            )}
          </div>
          <button
            className={`${styles.action} ${styles.actionPrimary}`}
            onClick={() => (hasNativeShare() ? nativeShare() : setShareOpen((v) => !v))}
            type="button"
            aria-haspopup="menu"
            aria-expanded={shareOpen}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" strokeLinecap="round" />
            </svg>
            Share
          </button>
          {shareOpen && (
            <div className={styles.shareMenu}>
              <a href={socialLinks.x} target="_blank" rel="noopener noreferrer">
                Share on X
              </a>
              <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer">
                Share on LinkedIn
              </a>
              <a href={socialLinks.reddit} target="_blank" rel="noopener noreferrer">
                Share on Reddit
              </a>
              <button type="button" onClick={() => navigator.clipboard?.writeText(shareUrl)}>
                Copy link
              </button>
            </div>
          )}
        </div>
      </div>

      {/* KPI tiles (GSC summary cards) */}
      <div className={styles.kpiRow}>
        <button
          type="button"
          className={`${styles.kpi} ${styles.kpiBlue}`}
          onClick={() => setTab('ranking')}
        >
          <span className={styles.kpiLabel}>Category leader</span>
          <span className={styles.kpiValue}>{rows[0].name}</span>
          <span className={styles.kpiSub}>{rows[0].vendor}</span>
        </button>
        <button
          type="button"
          className={`${styles.kpi} ${styles.kpiPurple}`}
          onClick={() => setMetric('share')}
        >
          <span className={styles.kpiLabel}>Leader share</span>
          <span className={styles.kpiValue}>{rows[0].share}%</span>
          <span className={styles.kpiSub}>of {data.label.toLowerCase()} demand</span>
        </button>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Total demand</span>
          <span className={styles.kpiValue}>{formatVolume(totalDemand)}</span>
          <span className={styles.kpiSub}>searches / month</span>
        </div>
        <button
          type="button"
          className={styles.kpi}
          onClick={() => setTab('movers')}
          disabled={!risers[0]}
        >
          <span className={styles.kpiLabel}>Fastest riser</span>
          <span className={styles.kpiValue}>
            {risers[0] ? risers[0].name : '—'}
            {risers[0] && <span className={styles.kpiUp}> +{risers[0].momentum}%</span>}
          </span>
          <span className={styles.kpiSub}>3-month momentum</span>
        </button>
      </div>

      {/* chart */}
      <div className={styles.chartCard}>
        <div className={styles.chartHead}>
          <span className={styles.meta}>
            Real Google search demand · <strong>{scopeName}</strong> · data through{' '}
            <strong>{monthLabel(dataThrough)}</strong>
          </span>
        </div>
        <LineChart months={data.months} rows={rows} metric={metric} />
        <div className={styles.legend}>
          {rows.slice(0, 6).map((r, i) => (
            <span key={r.name} className={styles.legItem}>
              <span className={styles.dot} style={{ background: PALETTE[i] }} />
              {r.name}
            </span>
          ))}
        </div>
      </div>

      {/* GSC-style tab bar */}
      <div className={styles.gscTabs} role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'ranking'}
          className={`${styles.gscTab} ${tab === 'ranking' ? styles.gscTabActive : ''}`}
          onClick={() => setTab('ranking')}
        >
          Ranking
        </button>
        <button
          role="tab"
          aria-selected={tab === 'movers'}
          className={`${styles.gscTab} ${tab === 'movers' ? styles.gscTabActive : ''}`}
          onClick={() => setTab('movers')}
        >
          Risers &amp; decliners
        </button>
        <button
          role="tab"
          aria-selected={tab === 'country'}
          className={`${styles.gscTab} ${tab === 'country' ? styles.gscTabActive : ''}`}
          onClick={() => setTab('country')}
        >
          By country
        </button>
      </div>

      {tab === 'ranking' && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.rank}>#</th>
                  <th>{data.unit}</th>
                  <th style={{ textAlign: 'right' }}>
                    ↓ {metric === 'share' ? 'Share' : 'Monthly searches'}
                  </th>
                  <th>Relative</th>
                  <th>12-mo trend</th>
                  <th style={{ textAlign: 'right' }}>MoM</th>
                  <th style={{ textAlign: 'right' }}>YoY</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.name}>
                    <td className={styles.rank}>
                      {r.rank <= 3 ? (
                        <span className={`${styles.rankMedal} ${styles[`r${r.rank}` as 'r1']}`}>
                          {r.rank}
                        </span>
                      ) : (
                        r.rank
                      )}
                    </td>
                    <td>
                      <div className={styles.brandCell}>
                        <span className={styles.brandName}>{r.name}</span>
                        <span className={styles.vendor}>{r.vendor}</span>
                      </div>
                    </td>
                    <td className={styles.num}>
                      {metric === 'share' ? `${r.share}%` : formatVolume(r.avg)}
                    </td>
                    <td>
                      <div className={styles.barWrap}>
                        <div className={styles.barTrack}>
                          <div
                            className={styles.barFill}
                            style={{
                              width: `${Math.max(3, (Math.sqrt(r.avg) / maxSqrt) * 100)}%`,
                              background: PALETTE[i] ?? 'var(--cmp-accent, #4285f4)',
                            }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      <Sparkline data={r.monthly} color={PALETTE[i] ?? '#4285f4'} />
                    </td>
                    <td className={`${styles.num} ${styles.delta} ${deltaClass(r.mom)}`}>
                      {deltaText(r.mom)}
                    </td>
                    <td className={`${styles.num} ${styles.delta} ${deltaClass(r.yoy)}`}>
                      {deltaText(r.yoy)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.tableFoot}>
            <span className={styles.meta}>
              {rows.length} tracked · Relative bars use a perceptual (√) scale so smaller tools stay
              visible; Share and Monthly-searches are exact.
            </span>
            <button className={styles.action} onClick={downloadImage} type="button">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <circle cx="8.5" cy="9.5" r="1.5" />
                <path d="M4 17l5-5 4 4 3-3 4 4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Save winners as image
            </button>
          </div>
        </>
      )}

      {tab === 'movers' && (
        <div className={styles.movers}>
          <div className={styles.moverCard}>
            <p className={styles.moverHead}>▲ Biggest risers · 3-month momentum</p>
            {risers.length === 0 && <p className={styles.meta}>No significant movers.</p>}
            {risers.map((r) => (
              <div key={r.name} className={styles.moverRow}>
                <span className={styles.moverName}>{r.name}</span>
                <span className={`${styles.moverPct} ${styles.up}`}>+{r.momentum}%</span>
              </div>
            ))}
          </div>
          <div className={styles.moverCard}>
            <p className={styles.moverHead}>▼ Biggest decliners · 3-month momentum</p>
            {decliners.length === 0 && <p className={styles.meta}>No significant movers.</p>}
            {decliners.map((r) => (
              <div key={r.name} className={styles.moverRow}>
                <span className={styles.moverName}>{r.name}</span>
                <span className={`${styles.moverPct} ${styles.down}`}>{r.momentum}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'country' && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Country</th>
                <th>#1 {data.unit}</th>
                <th style={{ textAlign: 'right' }}>Share</th>
                <th>Runner-up</th>
              </tr>
            </thead>
            <tbody>
              {byCountry.map((c) => (
                <tr key={c.code}>
                  <td>
                    <span className={styles.brandName}>{c.name}</span>
                  </td>
                  <td>
                    <span className={styles.brandName}>{c.leader.name}</span>{' '}
                    <span className={styles.vendor}>{c.leader.vendor}</span>
                  </td>
                  <td className={styles.num}>{c.leader.share}%</td>
                  <td>{c.second ? c.second.name : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
